import {
  ISquizCategory,
  ISquizFacet,
  ISquizFacetValue,
  ISquizCluster,
  ISquizSearchResponse,
  ISquizSearchResult,
  ISquizSuggestResponse,
  squizBaseUrl,
} from "./squiz";
import { isNative } from "@origin-digital/platform-helpers";
import { search, suggest } from "../contract";
import { config } from "../config";

const BFF_SEARCH_ENDPOINT = "/api/search";

export interface ISearchResult {
  rank: number;
  title: string;
  url: string;
  summary: string;
  allSummaryText: string | null;
  imageUrl?: string;
  description?: string;
  displayUrl: string;
  clickTrackingUrl: string;
}

export interface ISearchResponse {
  pagination?: IPaginationBlock;
  resultsSummary: ISearchResultsSummary;
  results: ISearchResult[];
  substituteQuery?: string;
  spellingSuggestion?: {
    url: string;
    text: string;
  } | null;
  facets: ISearchFacet[];
  question: {
    query: string;
    originalQuery: string;
  };
  relatedSearches: IRelatedSearch[];
}

export interface IRelatedSearch {
  href: string;
  count: number;
  label: string;
  query: string;
}

export interface ISearchResultsSummary {
  fullyMatching: number;
  partiallyMatching: number;
  totalMatching: number;
  currStart: number;
  currEnd: number;
}

export interface ISearchFacetValue {
  label: string;
  count: number;
  selected: boolean;
  toggleUrl: string;
}

export interface ISearchFacet {
  name: string;
  unselectAllUrl: string;
  values: ISearchFacetValue[];
}

export interface IPaginationBlock {
  first: IPagination;
  previous: IPagination | null;
  next: IPagination;
  pages: IPagination[];
}

export interface IPagination {
  label: string;
  url: string;
  selected: boolean;
}

const getEmptySearchResponse = (
  queryStr: string | undefined
): ISearchResponse => ({
  resultsSummary: {
    fullyMatching: 0,
    partiallyMatching: 0,
    totalMatching: 0,
    currStart: 0,
    currEnd: 0,
  },
  results: [],
  spellingSuggestion: null,
  facets: [],
  question: {
    query: queryStr || "!null",
    originalQuery: queryStr || "!null",
  },
  relatedSearches: [],
});

type QueryValue = string | number | boolean | undefined;

function buildQueryString(params: Record<string, QueryValue>): string {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      queryParams.set(key, String(value));
    }
  });

  return queryParams.toString();
}

type BffSearchPayload = {
  question?: {
    query?: string;
    originalQuery?: string;
  };
  results?: BffSearchResult[];
  relatedSearches?: Array<{
    label?: string;
    query?: string;
  }>;
  substituteQuery?: string;
  spellingSuggestion?: {
    label?: string;
    query?: string;
  } | null;
};

type BffSearchResult = {
  rank?: number;
  title?: string;
  url?: string;
  summary?: string;
  allSummaryText?: string | null;
  imageUrl?: string;
  description?: string;
  displayUrl?: string;
  clickTrackingUrl?: string;
};

function normalizeSearchResults(results?: BffSearchResult[]): ISearchResult[] {
  return Array.isArray(results)
    ? results.map((result) => ({
        rank: result.rank ?? 0,
        title: result.title || "",
        url: result.url || "",
        summary: result.summary || "",
        allSummaryText: result.allSummaryText ?? null,
        imageUrl: result.imageUrl,
        description: result.description,
        displayUrl: result.displayUrl || "",
        clickTrackingUrl: result.clickTrackingUrl || "",
      }))
    : [];
}

function normalizeRelatedSearches(
  searches?: Array<{ label?: string; query?: string }>
): IRelatedSearch[] {
  return Array.isArray(searches)
    ? searches.map((relatedSearch) => ({
        href: "",
        count: 0,
        label: relatedSearch.label || "",
        query: relatedSearch.query || "",
      }))
    : [];
}

function buildSpellingUrl(
  suggestion: { label?: string; query?: string } | null | undefined
): { text: string; url: string } | null {
  if (!suggestion) return null;
  return {
    text: suggestion.label || "",
    url: `${BFF_SEARCH_ENDPOINT}?${buildQueryString({
      q: suggestion.query || "",
      isNative: isNative(),
    })}`,
  };
}

function normalizeBffSearchResponse(
  payload: BffSearchPayload,
  queryStr: string
): ISearchResponse {
  const results = normalizeSearchResults(payload.results);
  const relatedSearches = normalizeRelatedSearches(payload.relatedSearches);

  return {
    question: {
      query: payload.question?.query || queryStr,
      originalQuery: payload.question?.originalQuery || queryStr,
    },
    // The current BFF contract does not expose overall match counts, so these
    // summary values are derived from the current page payload only.
    resultsSummary: {
      fullyMatching: results.length,
      partiallyMatching: 0,
      totalMatching: results.length,
      currStart: results.length ? 1 : 0,
      currEnd: results.length,
    },
    results,
    relatedSearches,
    substituteQuery: payload.substituteQuery,
    spellingSuggestion: buildSpellingUrl(payload.spellingSuggestion),
    facets: [],
  };
}

function extractSearchPayload(response: unknown): BffSearchPayload | undefined {
  if (!response || typeof response !== "object") {
    return undefined;
  }

  if (
    "data" in response &&
    response.data &&
    typeof response.data === "object"
  ) {
    return response.data as BffSearchPayload;
  }

  // response is already BffSearchPayload shape (direct payload from contract client)
  return response as BffSearchPayload;
}

type SuggestionEntry = { key: string; label: string };
type SquizSuggestion = ISquizSuggestResponse[number];

function extractSuggestionEntry(entry: unknown): SuggestionEntry | null {
  if (!entry || typeof entry !== "object") {
    return null;
  }

  const rawEntry = entry as {
    key?: string;
    label?: string;
    data?: { key?: string; label?: string };
  };
  const suggestionEntry =
    rawEntry.data && typeof rawEntry.data === "object"
      ? rawEntry.data
      : rawEntry;

  if (!suggestionEntry.key || !suggestionEntry.label) {
    return null;
  }

  return {
    key: suggestionEntry.key,
    label: suggestionEntry.label,
  };
}

function toSquizSuggestion(suggestionEntry: SuggestionEntry): SquizSuggestion {
  return {
    key: suggestionEntry.key,
    disp: suggestionEntry.label,
    disp_t: "T" as const,
    wt: "",
    cat: "default",
    action: suggestionEntry.label,
    action_t: "Q" as const,
  };
}

function extractSuggestPayload(response: unknown): unknown[] {
  if (!response || typeof response !== "object") {
    return [];
  }

  const rawSuggestionPayload =
    "data" in response ? (response as { data: unknown }).data : response;

  return Array.isArray(rawSuggestionPayload) ? rawSuggestionPayload : [];
}

function normalizeSuggestEntries(entries: unknown[]): ISquizSuggestResponse {
  return entries
    .map(extractSuggestionEntry)
    .filter(
      (suggestionEntry): suggestionEntry is SuggestionEntry =>
        suggestionEntry !== null
    )
    .map(toSquizSuggestion);
}

export const doSearch = async (
  queryStr: string | undefined
): Promise<ISearchResponse> => {
  const requestQuery = queryStr || "";
  const normalizedQuery = queryStr || "!null";
  const isNativePlatform = isNative();

  try {
    const response = await search(
      config.apiBasePath,
      {
        q: requestQuery,
        isNative: isNativePlatform,
      },
      undefined
    );

    const payload = extractSearchPayload(response);
    if (!payload) {
      return getEmptySearchResponse(queryStr);
    }

    return normalizeBffSearchResponse(payload, normalizedQuery);
  } catch (error) {
    console.error("Error searching via contract client", { error });
    return getEmptySearchResponse(queryStr);
  }
};

export const doSuggest = async (
  query: string
): Promise<ISquizSuggestResponse> => {
  if (!query) {
    return [];
  }

  try {
    const response = await suggest(
      config.apiBasePath,
      {
        q: query,
        isNative: isNative(),
      },
      undefined
    );

    const suggestionEntries = extractSuggestPayload(response);

    return normalizeSuggestEntries(suggestionEntries);
  } catch (error) {
    console.error("Error suggesting via contract client", { error });
    return [];
  }
};
function transformResults(squizResults: ISquizSearchResult[]): ISearchResult[] {
  return squizResults.map((squizResult) => {
    const imageUrl = squizResult.listMetadata?.image?.[0];
    const summary = squizResult.listMetadata?.c?.[0] ?? squizResult.summary;

    return {
      rank: squizResult.rank,
      url: squizResult.liveUrl,
      displayUrl: squizResult.displayUrl,
      allSummaryText: squizResult.allSummaryText,
      clickTrackingUrl: squizBaseUrl() + squizResult.clickTrackingUrl,
      title: squizResult.title,
      summary,
      imageUrl,
    };
  });
}

function transformFacets(squizFacets: ISquizFacet[]): ISearchFacet[] {
  return squizFacets
    .map((squizFacet) => ({
      name: squizFacet.name,
      unselectAllUrl: squizFacet.unselectAllUrl,
      values: squizFacet.allValues
        .filter((facetValue: ISquizFacetValue) => !!facetValue.count)
        .map((facetValue: ISquizFacetValue) => ({
          label: facetValue.label,
          count: facetValue.count,
          selected: facetValue.selected,
          toggleUrl: facetValue.toggleUrl,
        })),
    }))
    .filter((facet) => !!facet.values.length);
}

function extractRelatedSearches(
  squizCategories: ISquizCategory[]
): IRelatedSearch[] {
  return squizCategories.reduce((searches, category) => {
    searches.push(
      ...category.clusters.map((cluster: ISquizCluster) => ({
        href: cluster.href,
        count: cluster.count,
        label: cluster.label,
        query: cluster.query,
      }))
    );
    return searches;
  }, [] as IRelatedSearch[]);
}

function extractPagination(
  squizResponse: ISquizSearchResponse
): IPaginationBlock | undefined {
  const pagination = squizResponse?.response?.customData?.stencils?.pagination;

  if (
    pagination &&
    typeof pagination === "object" &&
    "first" in pagination &&
    "next" in pagination &&
    "pages" in pagination
  ) {
    return pagination as IPaginationBlock;
  }

  return undefined;
}

function extractSummary(
  squizResponse: ISquizSearchResponse
): ISearchResultsSummary {
  const squizResultsSummary =
    squizResponse?.response?.resultPacket?.resultsSummary;

  if (!squizResultsSummary) {
    return {
      fullyMatching: 0,
      partiallyMatching: 0,
      totalMatching: 0,
      currStart: 0,
      currEnd: 0,
    };
  }

  return {
    totalMatching: squizResultsSummary.totalMatching ?? 0,
    fullyMatching: squizResultsSummary.fullyMatching ?? 0,
    partiallyMatching: squizResultsSummary.partiallyMatching ?? 0,
    currStart: squizResultsSummary.currStart ?? 0,
    currEnd: squizResultsSummary.currEnd ?? 0,
  };
}

function extractQuestion(squizResponse: ISquizSearchResponse): {
  query: string;
  originalQuery: string;
} {
  const question = squizResponse?.question;

  return {
    query: question?.query ?? "!null",
    originalQuery: question?.originalQuery ?? "!null",
  };
}

function extractSubstituteQuery(
  squizResponse: ISquizSearchResponse
): string | undefined {
  return squizResponse?.response?.resultPacket?.qsups?.[0]?.query;
}

function extractSpellingSuggestion(squizResponse: ISquizSearchResponse):
  | {
      url: string;
      text: string;
    }
  | null
  | undefined {
  return squizResponse?.response?.resultPacket?.spell;
}

function extractFacetsData(squizResponse: ISquizSearchResponse): ISquizFacet[] {
  return squizResponse?.response?.facets ?? [];
}

export function transformSquizResponse(
  squizResponse: ISquizSearchResponse
): ISearchResponse {
  const pagination = extractPagination(squizResponse);
  const resultsSummary = extractSummary(squizResponse);
  const question = extractQuestion(squizResponse);
  const substituteQuery = extractSubstituteQuery(squizResponse);
  const spellingSuggestion = extractSpellingSuggestion(squizResponse);

  const squizResults = squizResponse?.response?.resultPacket?.results ?? [];
  const results = transformResults(squizResults);

  const squizRelatedSearches =
    squizResponse?.response?.resultPacket?.contextualNavigation?.categories ??
    [];
  const relatedSearches = extractRelatedSearches(squizRelatedSearches);

  const squizFacets = extractFacetsData(squizResponse);
  const facets = transformFacets(squizFacets);

  return {
    question,
    resultsSummary,
    results,
    relatedSearches,
    substituteQuery,
    spellingSuggestion,
    pagination,
    facets,
  };
}
